import { useState, useEffect } from 'react';
import { useSessionState } from '@/hooks/useSessionState';
import { SessionKey } from '@/types/storage';
import { useThemeColors } from '@/styles';
import { ScreenContainer } from '@/components/core/ScreenContainer';
import { ScreenState } from '@/types/screen.types';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/enums/UserRole';
import { useLearningProgressStatsQuery } from '@/hooks/queries/useLearningProgressStatsQuery';
import { useMyProgressQuery } from '@/hooks/queries/useMyProgressQuery';
import { QueryError } from '@/lib/execute-query';
import { ResponseCode } from '@/business/types/response-codes.types';
import { LearningProgressCharts } from './LearningProgressCharts';
import { LearningProgressAdminHeader } from './LearningProgressAdminHeader';
import { MyProgress } from '@/components/core';

export default function LearningProgress() {
  const colors = useThemeColors();
  const { user } = useAuth();
  const userRole = user?.role;
  const isStudent = userRole === UserRole.STUDENT;

  const [selectedUserId, setSelectedUserId] = useSessionState<
    string | undefined
  >(SessionKey.LB_LEARNING_PROGRESS_SELECTED_USER_ID, undefined);
  const [selectedRole, setSelectedRole] = useSessionState<string | undefined>(
    SessionKey.LB_LEARNING_PROGRESS_SELECTED_ROLE,
    undefined
  );

  const handleUserSelect = (userId: string | undefined) => {
    setSelectedUserId(userId);
    setSelectedRole(undefined);
  };

  const handleRoleSelect = (role: string | undefined) => {
    setSelectedRole(role);
    setSelectedUserId(undefined);
  };

  const {
    data: velocityData,
    isLoading,
    isError,
    error,
    refetch,
  } = useLearningProgressStatsQuery(selectedUserId, selectedRole);

  const { data: heatmapData } = useMyProgressQuery(
    selectedUserId,
    selectedRole
  );

  const [screenState, setScreenState] = useState<ScreenState>(
    ScreenState.LOADING
  );

  useEffect(() => {
    if (isLoading) {
      setScreenState(ScreenState.LOADING);
    } else if (isError) {
      setScreenState(ScreenState.ERROR);
    } else if (velocityData) {
      setScreenState(ScreenState.SUCCESS);
    }
  }, [isLoading, isError, velocityData]);

  const screenError =
    isError && error
      ? {
          code:
            error instanceof QueryError
              ? error.code
              : ResponseCode.INTERNAL_ERROR,
          message: 'Failed to load velocity stats. Please try again later.',
          canRetry: true,
        }
      : null;

  const isSelfView = !selectedUserId && !selectedRole;

  const getSubtitle = () => {
    if (isSelfView)
      return `Your activity over the last ${velocityData?.weeks.length ?? 5} weeks`;
    if (selectedRole) {
      const label = selectedRole === UserRole.STUDENT ? 'students' : 'mentors';
      return `Viewing all ${label}' activity`;
    }
    const userName = velocityData?.target_user?.display_name || 'user';
    return `Viewing ${userName}'s activity`;
  };

  return (
    <div
      className="flex flex-col gap-6 p-6 h-full overflow-y-auto"
      style={{ backgroundColor: colors.background }}
    >
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col gap-3">
          <p className="text-sm" style={{ color: colors.text.secondary }}>
            {getSubtitle()}
          </p>

          {!isStudent && (
            <LearningProgressAdminHeader
              selectedUserId={selectedUserId}
              selectedRole={selectedRole}
              onUserSelect={handleUserSelect}
              onRoleSelect={handleRoleSelect}
            />
          )}
        </div>

        {/* Content */}
        <ScreenContainer
          state={screenState}
          error={screenError}
          onRetry={() => refetch()}
          skeleton={{ type: 'dashboard' }}
        >
          {velocityData && (
            <div className="flex flex-col gap-6">
              <LearningProgressCharts
                weeks={velocityData.weeks}
                tasksByPriority={velocityData.tasks_by_priority ?? []}
                tasksByStatus={velocityData.tasks_by_status ?? []}
                tasksByReflection={velocityData.tasks_by_reflection ?? []}
              />

              {heatmapData?.my_progress &&
              heatmapData.my_progress.length > 0 ? (
                <div
                  className="rounded-xl shadow-soft p-5"
                  style={{ backgroundColor: colors.offWhite }}
                >
                  <h3
                    className="text-sm font-medium mb-4"
                    style={{ color: colors.text.secondary }}
                  >
                    Heatmap
                  </h3>
                  <MyProgress
                    data={heatmapData.my_progress}
                    weeks={heatmapData.weeks}
                  />
                </div>
              ) : null}
            </div>
          )}
        </ScreenContainer>
      </div>
    </div>
  );
}
